import React, {
    forwardRef,
    HTMLProps,
    useState,
    useEffect,
    useRef
} from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import { Transition } from 'react-transition-group';
import { usePopper } from 'react-popper';
import {
    Placement,
    Rect,
    Options as PopperOptions
} from '@popperjs/core';
import { ROOT_RENDER_ID } from 'project-constants';
import './style.scss';

export interface IManualPopover extends HTMLProps<HTMLDivElement> {
    open: boolean;
    onClickAway: () => void;
    modifiers: PopperOptions['modifiers'];
    targetElement: React.MutableRefObject<HTMLDivElement>;
    placement?: Placement
}

export const ManualPopover = (props: IManualPopover) => {
    const {
        targetElement,
        className,
        children,
        placement,
        modifiers,
        open = false
    } = props;

    /**
     * Тут указывается 2 рефа, которые будут указывать на один и тот же объект.
     * В чем именно разница между такими объявлениями я не знаю, но если не определить реф
     * именно так как указано в доках popperjs (первый вариант через useState), то
     * отображаться нормально popper будет только со 2 раза (поппер не будет правильно позиционироваться
     * и встанет на свое место только со второго открытия поппера).
     *
     * Второй реф нужен для того чтобы непосредственно работать с этим элементом как с любым другим
     * DOM элементом. Далее по коду идет проверка на клик по window чтобы определить нужно ли
     * закрывать поппер или нет (если кликнули в любую область за пределы поппера, то поппер нужно закрыть).
     * В обработчике `handleOutsideClick` будет работать только второй вариант ref (который был объявлен через `useRef`)
     * `_popperElementForPopperHook_` там будет либо null либо undefined
     */
    const [ _popperElementForPopperHook_, _setPopperElementForPopperHook_ ] = useState<HTMLDivElement>(null);
    const popperRef = useRef<HTMLDivElement>(null);

    const { styles, attributes } = usePopper(
        targetElement.current,
        _popperElementForPopperHook_,
        {
            placement,
            modifiers
        }
    );

    const isQuestionMarkClick = (event: MouseEvent) => (
            targetElement?.current === event.target
            || targetElement?.current.contains(event.target as Node)
    );

    const isOuterClick = (event: MouseEvent) => !popperRef.current?.contains(event.target as Node);

    const handleOutsideClick = (event: MouseEvent) => {
        if (!isQuestionMarkClick(event) && isOuterClick(event)) {
            props.onClickAway();
        }
    };

    /* componentDidMount */
    useEffect(() => {
        document.addEventListener('click', handleOutsideClick);

        /* componentWillUnmount */
        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, []);

    return (
        <Transition in={open} timeout={500} mountOnEnter unmountOnExit>
            {state => (
                ReactDOM.createPortal(
                    <div
                        className={clsx('oms-popover', className, state)}
                        ref={(el) => {
                            _setPopperElementForPopperHook_(el);
                            popperRef.current = el;
                        }}
                        style={styles.popper}
                        {...attributes.popper}
                    >
                        {children}
                    </div>,
                    document.getElementById(ROOT_RENDER_ID)
                )
            )}
        </Transition>
    );
};
